import React from 'react';
import Icon from './Icon';
import './Tree.css';

/*
 * Tree — expandable node navigator (Customer > Site > Gate).
 * Controlled expansion + selection.
 *
 * Props:
 *   nodes    : [{
 *     id,              // unique key
 *     label,           // node label (node)
 *     icon,            // optional leading <Icon/> (or any node)
 *     count,           // optional count badge (e.g. # of children/events)
 *     action,          // optional inline action node, right-aligned, shown on hover/focus
 *     children,        // optional array of child nodes (makes it expandable)
 *     disabled,
 *   }]
 *   expanded : Set | array | object-map of expanded node ids (controlled)
 *   onToggle : (id, node) => void        (toggle expand/collapse)
 *   selectedId : currently selected node id
 *   onSelect : (id, node) => void        (row click / Enter)
 *   defaultExpandAll : if `expanded` is omitted, render fully expanded (uncontrolled-ish, read-only)
 */
function isExpanded(expanded, id) {
  if (!expanded) return false;
  if (expanded instanceof Set) return expanded.has(id);
  if (Array.isArray(expanded)) return expanded.indexOf(id) !== -1;
  return !!expanded[id];
}

function TreeNode({ node, depth, expanded, onToggle, selectedId, onSelect, allOpen }) {
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const open = allOpen ? true : isExpanded(expanded, node.id);
  const selected = selectedId != null && selectedId === node.id;

  const onRowClick = () => {
    if (node.disabled) return;
    if (onSelect) onSelect(node.id, node);
  };
  const onCaretClick = (e) => {
    e.stopPropagation();
    if (hasChildren && onToggle) onToggle(node.id, node);
  };
  const onKeyDown = (e) => {
    if (node.disabled) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(); }
    else if (e.key === 'ArrowRight' && hasChildren && !open) { e.preventDefault(); onToggle && onToggle(node.id, node); }
    else if (e.key === 'ArrowLeft' && hasChildren && open) { e.preventDefault(); onToggle && onToggle(node.id, node); }
  };

  return (
    <li className="ui-tree__li" role="treeitem" aria-expanded={hasChildren ? open : undefined} aria-selected={selected || undefined}>
      <div
        className={`ui-tree__row${selected ? ' is-selected' : ''}${node.disabled ? ' is-disabled' : ''}`}
        style={{ paddingLeft: 8 + depth * 16 }}
        onClick={onRowClick}
        onKeyDown={onKeyDown}
        tabIndex={node.disabled ? undefined : 0}
      >
        <button
          type="button"
          className={`ui-tree__caret${hasChildren ? '' : ' is-leaf'}`}
          onClick={onCaretClick}
          tabIndex={-1}
          aria-hidden={!hasChildren}
          aria-label={hasChildren ? (open ? 'Collapse' : 'Expand') : undefined}
        >
          {hasChildren && <Icon name={open ? 'chevronDown' : 'chevronRight'} size={15} />}
        </button>
        {node.icon != null && <span className="ui-tree__icon">{node.icon}</span>}
        <span className="ui-tree__label truncate">{node.label}</span>
        {node.count != null && <span className="ui-tree__count num">{node.count}</span>}
        {node.action != null && <span className="ui-tree__action" onClick={(e) => e.stopPropagation()}>{node.action}</span>}
      </div>
      {hasChildren && open && (
        <ul className="ui-tree__children" role="group">
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              selectedId={selectedId}
              onSelect={onSelect}
              allOpen={allOpen}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function Tree({
  nodes = [],
  expanded,
  onToggle,
  selectedId,
  onSelect,
  defaultExpandAll = false,
  className = '',
  ...rest
}) {
  const allOpen = defaultExpandAll && expanded == null;
  return (
    <ul className={`ui-tree${className ? ' ' + className : ''}`} role="tree" {...rest}>
      {nodes.map((node) => (
        <TreeNode
          key={node.id}
          node={node}
          depth={0}
          expanded={expanded}
          onToggle={onToggle}
          selectedId={selectedId}
          onSelect={onSelect}
          allOpen={allOpen}
        />
      ))}
    </ul>
  );
}
